import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  PeopleAltOutlined as PeopleIcon,
  RotateRight as RotateIcon,
} from '@mui/icons-material';
import TablesService from '../../services/TablesService';
import { setTablesEditorDirty } from './editorGuard';
import SnackbarUtils from '../../utils/SnackbarUtils';
import {
  CELL,
  SHAPES,
  buildLayoutPayload,
  buildMetaChanges,
  canvasSize,
  clampRect,
  describeTablesError,
  findFreeSpot,
  geometryChanged,
  hasCollision,
  nextTableName,
  metaChanged,
  shapeChangeGeometry,
  shapeDefaults,
  validateMeta,
} from './tablesUtils';

const NameDialog = ({ open, title, initialValue, onClose, onSubmit }) => {
  const [value, setValue] = useState(initialValue || '');
  useEffect(() => {
    if (open) setValue(initialValue || '');
  }, [open, initialValue]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Nombre"
          value={value}
          inputProps={{ maxLength: 100 }}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && value.trim()) onSubmit(value.trim());
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!value.trim()} onClick={() => onSubmit(value.trim())}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const tableSx = (table, selected, dirty) => ({
  position: 'absolute',
  left: table.posX * CELL,
  top: table.posY * CELL,
  width: table.width * CELL,
  height: table.height * CELL,
  boxSizing: 'border-box',
  borderRadius: table.shape === 'ROUND' ? '50%' : '8px',
  border: '2px solid',
  borderColor: selected ? 'primary.main' : dirty ? 'warning.main' : 'divider',
  borderStyle: dirty && !selected ? 'dashed' : 'solid',
  bgcolor: selected ? 'primary.light' : 'background.paper',
  color: selected ? 'primary.contrastText' : 'text.primary',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'grab',
  userSelect: 'none',
  touchAction: 'none',
  boxShadow: selected ? 3 : 1,
  '&:active': { cursor: 'grabbing' },
});

const FloorEditor = ({ locationId, areas, onReload }) => {
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  // Cambios sin guardar: tableId -> { posX, posY, width, height, rotation, name, capacity, shape } (parcial). Se ven al
  // instante en el plano y se guardan juntos con "Guardar plano".
  const [draft, setDraft] = useState({});
  const [areaDialog, setAreaDialog] = useState(null); // { mode: 'create' | 'rename' }
  const [saving, setSaving] = useState(false);
  const interaction = useRef(null);

  const area = useMemo(() => areas.find((item) => item.id === selectedAreaId) || areas[0] || null, [areas, selectedAreaId]);

  useEffect(() => {
    if (area && area.id !== selectedAreaId) setSelectedAreaId(area.id);
  }, [area, selectedAreaId]);

  // Mesas del salón con los cambios todavía no guardados.
  const tables = useMemo(
    () => (area ? area.tables.map((table) => ({ ...table, ...(draft[table.id] || {}) })) : []),
    [area, draft]
  );
  const originalTables = area?.tables || [];
  const layoutPayload = useMemo(() => buildLayoutPayload(originalTables, tables), [originalTables, tables]);
  const metaChanges = useMemo(() => buildMetaChanges(originalTables, tables), [originalTables, tables]);
  const dirty = layoutPayload !== null || metaChanges.length > 0;
  const selectedTable = tables.find((table) => table.id === selectedTableId) || null;
  const size = useMemo(() => canvasSize(tables), [tables]);

  useEffect(() => {
    setTablesEditorDirty(dirty);
    return () => setTablesEditorDirty(false);
  }, [dirty]);

  const confirmDiscard = () => !dirty || window.confirm('Hay cambios sin guardar. ¿Descartarlos?');

  const selectArea = (areaId) => {
    if (areaId === selectedAreaId || !confirmDiscard()) return;
    setDraft({});
    setSelectedTableId(null);
    setSelectedAreaId(areaId);
  };

  const run = async (action, successMessage) => {
    setSaving(true);
    try {
      const result = await action();
      if (successMessage) SnackbarUtils.success(successMessage);
      return { ok: true, result };
    } catch (error) {
      SnackbarUtils.error(describeTablesError(error));
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  // Aplica cambios parciales al borrador de una mesa sin pisar los demás (posición, tamaño o datos).
  const patchDraft = (id, changes) => setDraft((previous) => ({ ...previous, [id]: { ...previous[id], ...changes } }));

  // ---- Arrastrar y redimensionar ----
  const startInteraction = (event, table, mode) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedTableId(table.id);
    interaction.current = {
      mode,
      id: table.id,
      startX: event.clientX,
      startY: event.clientY,
      origin: { x: table.posX, y: table.posY, w: table.width, h: table.height },
    };
  };

  const moveInteraction = (event) => {
    const current = interaction.current;
    if (!current) return;
    const dx = Math.round((event.clientX - current.startX) / CELL);
    const dy = Math.round((event.clientY - current.startY) / CELL);
    const { x, y, w, h } = current.origin;
    const rect = clampRect(current.mode === 'move' ? { x: x + dx, y: y + dy, w, h } : { x, y, w: w + dx, h: h + dy });
    // No se puede soltar una mesa encima de otra: el movimiento queda en la última posición libre.
    if (hasCollision(rect, tables, current.id)) return;
    patchDraft(current.id, { posX: rect.x, posY: rect.y, width: rect.w, height: rect.h, rotation: 0 });
  };

  const endInteraction = () => {
    interaction.current = null;
  };

  const updateSelectedGeometry = (changes) => {
    if (!selectedTable) return;
    const rect = clampRect({
      x: selectedTable.posX,
      y: selectedTable.posY,
      w: changes.width ?? selectedTable.width,
      h: changes.height ?? selectedTable.height,
    });
    if (hasCollision(rect, tables, selectedTable.id)) {
      SnackbarUtils.warning('No hay lugar para ese tamaño en esa posición.');
      return;
    }
    patchDraft(selectedTable.id, { posX: rect.x, posY: rect.y, width: rect.w, height: rect.h, rotation: 0 });
  };

  const changeShape = (shape) => {
    if (!selectedTable) return;
    patchDraft(selectedTable.id, { shape, ...shapeChangeGeometry(selectedTable, shape, tables) });
  };

  // ---- Acciones contra el backend ----
  const saveLayout = async () => {
    if (!area || !dirty) return;
    const problem = validateMeta(metaChanges);
    if (problem) {
      SnackbarUtils.warning(problem);
      return;
    }
    const { ok } = await run(async () => {
      // Primero nombre, capacidad y forma; después las posiciones. Si algo falla (ej. nombre repetido) el borrador se
      // conserva completo para corregirlo y volver a guardar.
      for (const change of metaChanges) {
        // eslint-disable-next-line no-await-in-loop
        await TablesService.updateTable(change.id, { name: change.name, capacity: change.capacity, shape: change.shape });
      }
      if (layoutPayload) await TablesService.saveLayout(area.id, layoutPayload);
    }, 'Plano guardado');
    if (ok) {
      setDraft({});
      await onReload();
    }
  };

  const addTable = async () => {
    if (!area) return;
    const defaults = shapeDefaults('SQUARE');
    const spot = findFreeSpot(tables, defaults.width, defaults.height);
    const { ok, result } = await run(() =>
      TablesService.createTable({
        areaId: area.id,
        name: nextTableName(tables),
        capacity: 4,
        shape: 'SQUARE',
        posX: spot.x,
        posY: spot.y,
        width: defaults.width,
        height: defaults.height,
      })
    );
    if (ok) {
      await onReload();
      setSelectedTableId(result);
    }
  };

  const deleteTable = async () => {
    if (!selectedTable || !window.confirm(`¿Eliminar la mesa ${selectedTable.name}?`)) return;
    const { ok } = await run(() => TablesService.deleteTable(selectedTable.id), 'Mesa eliminada');
    if (ok) {
      setDraft((previous) => {
        const { [selectedTable.id]: _removed, ...rest } = previous;
        return rest;
      });
      setSelectedTableId(null);
      await onReload();
    }
  };

  const submitArea = async (name) => {
    const mode = areaDialog?.mode;
    setAreaDialog(null);
    if (mode === 'create') {
      const { ok, result } = await run(() => TablesService.createArea({ locationId, name }), 'Salón creado');
      if (ok) {
        await onReload();
        setSelectedAreaId(result);
      }
    } else if (area) {
      const { ok } = await run(() => TablesService.updateArea(area.id, { name, sortOrder: null }), 'Salón actualizado');
      if (ok) await onReload();
    }
  };

  const deleteArea = async () => {
    if (!area || !window.confirm(`¿Eliminar el salón ${area.name} y todas sus mesas?`)) return;
    const { ok } = await run(() => TablesService.deleteArea(area.id), 'Salón eliminado');
    if (ok) {
      setDraft({});
      setSelectedTableId(null);
      setSelectedAreaId(null);
      await onReload();
    }
  };

  const renderTable = (table) => {
      const original = originalTables.find((item) => item.id === table.id);
      const isDirty = original ? geometryChanged(original, table) || metaChanged(original, table) : false;
      const selected = table.id === selectedTableId;
      return (
        <Box
          key={table.id}
          data-testid={`table-${table.id}`}
          sx={tableSx(table, selected, isDirty)}
          onPointerDown={(event) => startInteraction(event, table, 'move')}
          onPointerMove={moveInteraction}
          onPointerUp={endInteraction}
          onPointerCancel={endInteraction}
        >
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.1} noWrap sx={{ maxWidth: '90%' }}>
            {table.name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ opacity: 0.8 }}>
            <PeopleIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption">{table.capacity}</Typography>
          </Stack>
          {selected && (
            <Box
              data-testid="resize-handle"
              sx={{
                position: 'absolute',
                right: -6,
                bottom: -6,
                width: 14,
                height: 14,
                borderRadius: '3px',
                bgcolor: 'primary.main',
                cursor: 'nwse-resize',
                touchAction: 'none',
              }}
              onPointerDown={(event) => startInteraction(event, table, 'resize')}
              onPointerMove={moveInteraction}
              onPointerUp={endInteraction}
              onPointerCancel={endInteraction}
            />
          )}
        </Box>
      );
  };

  if (!area) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Todavía no hay salones en esta sucursal
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Creá el primero (por ejemplo "Salón" o "Terraza") para empezar a dibujar las mesas.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAreaDialog({ mode: 'create' })}>
          Crear salón
        </Button>
        <NameDialog open={Boolean(areaDialog)} title="Nuevo salón" onClose={() => setAreaDialog(null)} onSubmit={submitArea} />
      </Paper>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Tabs value={area.id} onChange={(_, value) => selectArea(value)} variant="scrollable" scrollButtons="auto" sx={{ flexGrow: 1, minWidth: 0 }}>
          {areas.map((item) => (
            <Tab key={item.id} value={item.id} label={item.name} />
          ))}
        </Tabs>
        <Tooltip title="Nuevo salón">
          <IconButton onClick={() => setAreaDialog({ mode: 'create' })} disabled={saving}><AddIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Renombrar salón">
          <IconButton onClick={() => setAreaDialog({ mode: 'rename' })} disabled={saving}><EditOutlinedIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Eliminar salón">
          <IconButton onClick={deleteArea} disabled={saving}><DeleteOutlineIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addTable} disabled={saving}>
          Agregar mesa
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {dirty && <Chip color="warning" size="small" label="Cambios sin guardar" />}
        <Button
          disabled={!dirty || saving}
          onClick={() => {
            setDraft({});
          }}
        >
          Descartar
        </Button>
        <Button variant="contained" disabled={!dirty || saving} onClick={saveLayout}>
          {saving ? <CircularProgress size={20} /> : 'Guardar plano'}
        </Button>
      </Stack>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0, width: '100%', overflow: 'auto', maxHeight: '70vh' }}>
          <Box
            data-testid="floor-canvas"
            onPointerDown={() => setSelectedTableId(null)}
            sx={{
              position: 'relative',
              width: size.cols * CELL,
              height: size.rows * CELL,
              backgroundImage:
                'linear-gradient(to right, rgba(128,128,128,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(128,128,128,0.18) 1px, transparent 1px)',
              backgroundSize: `${CELL}px ${CELL}px`,
            }}
          >
            {tables.map(renderTable)}
            {tables.length === 0 && (
              <Typography color="text.secondary" sx={{ position: 'absolute', top: 16, left: 16 }}>
                Agregá una mesa y arrastrala a su lugar.
              </Typography>
            )}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          {selectedTable ? (
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                Mesa {selectedTable.name}
              </Typography>
              <TextField
                label="Nombre"
                size="small"
                value={selectedTable.name}
                inputProps={{ maxLength: 50 }}
                onChange={(event) => patchDraft(selectedTable.id, { name: event.target.value })}
              />
              <TextField
                label="Capacidad"
                size="small"
                type="number"
                value={selectedTable.capacity}
                inputProps={{ min: 1, max: 50 }}
                onChange={(event) => patchDraft(selectedTable.id, { capacity: event.target.value })}
              />
              <FormControl size="small">
                <InputLabel id="shape-label">Forma</InputLabel>
                <Select labelId="shape-label" label="Forma" value={selectedTable.shape} onChange={(event) => changeShape(event.target.value)}>
                  {SHAPES.map((shape) => (
                    <MenuItem key={shape.value} value={shape.value}>{shape.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Ancho"
                  size="small"
                  type="number"
                  value={selectedTable.width}
                  inputProps={{ min: 1, max: 40 }}
                  onChange={(event) => updateSelectedGeometry({ width: Number(event.target.value) })}
                />
                <TextField
                  label="Alto"
                  size="small"
                  type="number"
                  value={selectedTable.height}
                  inputProps={{ min: 1, max: 40 }}
                  onChange={(event) => updateSelectedGeometry({ height: Number(event.target.value) })}
                />
              </Stack>
              <Button
                size="small"
                startIcon={<RotateIcon />}
                onClick={() => updateSelectedGeometry({ width: selectedTable.height, height: selectedTable.width })}
              >
                Rotar
              </Button>
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={saving} onClick={deleteTable}>
                Eliminar mesa
              </Button>
            </Stack>
          ) : (
            <Typography color="text.secondary" variant="body2">
              Seleccioná una mesa para editar su nombre, capacidad, forma y tamaño: los cambios se ven al instante. Arrastrala para moverla y usá el
              cuadrito de la esquina para redimensionarla. Todo se guarda junto con "Guardar plano".
            </Typography>
          )}
        </Paper>
      </Box>

      <NameDialog
        open={Boolean(areaDialog)}
        title={areaDialog?.mode === 'create' ? 'Nuevo salón' : 'Renombrar salón'}
        initialValue={areaDialog?.mode === 'rename' ? area.name : ''}
        onClose={() => setAreaDialog(null)}
        onSubmit={submitArea}
      />
    </Box>
  );
};

export default FloorEditor;
